/* DAILE v2 (dv) — glass optics. Port of the MVP glass-optics.js.
 *
 * Every element marked [data-dv-glass] (a pill or circle: radius = height/2)
 * gets a <span class="dv-glass-material"> prepended and the class
 * .dv-glass-mounted. In Chromium an SVG feDisplacementMap filter refracts the
 * rim of the backdrop while the centre is frosted; the maps are regenerated
 * only when the element changes size (ResizeObserver). Other browsers, and
 * reduced transparency / increased contrast, keep the CSS blur or solid glass
 * defined in dv-base.css.
 *
 * Optional value: data-dv-glass="32" = frost stdDeviation (MVP header 32,
 * NFC tabs 27; default 27).
 *
 * Optional optics — "liquid" lens (round 5, rebuilt in round 6 for a much
 * stronger refraction; the header pill in sections/dv-header.liquid is the
 * reference). Any of rim / dispersion / saturate switches a pill to these
 * optics; pills without them keep the defaults above, unchanged:
 *   data-dv-glass-rim=".5"         width of the lens band: a fraction of the
 *                                  height (< 1) or pixels (>= 1), at most
 *                                  half the height (.5 = the whole pill is
 *                                  one lens)
 *   data-dv-glass-bend="1"         how far the band reaches in at the edge:
 *                                  <= 1 = share of the no-mirror limit
 *                                  (1 = strongest), > 1 = pixels (capped at
 *                                  that limit). The band is a convex lens
 *                                  profile whose mapping never turns back,
 *                                  so the picture is magnified and bent but
 *                                  never flips, however strong the bend
 *   data-dv-glass-dispersion=".1"  chromatic split: red bends that much
 *                                  more, blue that much less (0..0.3; the
 *                                  limit above already leaves room for red)
 *   data-dv-glass-saturate="1.5"   vibrancy of the whole backdrop (default 1)
 *   data-dv-glass-clear=".45"      share of the band (from the edge) that
 *                                  stays sharp before it melts into the
 *                                  frost (default .42)
 * The frosted centre is taken from the refracted image, so the band and the
 * centre read as one lens. The header's full set (mirror it elsewhere):
 *   data-dv-glass="8" data-dv-glass-rim=".5" data-dv-glass-bend="1"
 *   data-dv-glass-dispersion=".1" data-dv-glass-saturate="1.5"
 *   data-dv-glass-clear=".45"
 * (the header also thins its tint towards the rim, dv-header.css — without
 * that, a thick tint hides most of the lens).
 *
 * Also sets html[data-dv-glass-optics="refraction|blur"] and
 * html.dv-opaque-glass (reduced transparency or increased contrast).
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const doc = document;
  const html = doc.documentElement;
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = (name, attrs = {}) => {
    const node = doc.createElementNS(ns, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    return node;
  };

  const reduceTransparency = matchMedia('(prefers-reduced-transparency: reduce)');
  const highContrast = matchMedia('(prefers-contrast: more)');
  const isOpaque = () => reduceTransparency.matches || highContrast.matches;

  // Round 7 (performance): the lens is GPU work on every frame the page
  // moves under it. Devices with little memory or few cores (navigator.
  // deviceMemory / hardwareConcurrency; desktops and current phones report
  // 8) get it lighter: <= 4 -> no chromatic split (one displacement pass
  // instead of three, the bend stays), <= 2 -> the CSS blur glass that
  // Safari shows. Unknown values count as capable.
  const memory = Number(navigator.deviceMemory) || 8;
  const cores = Number(navigator.hardwareConcurrency) || 8;
  const lowEnd = memory <= 4 || cores <= 4;
  const veryLowEnd = memory <= 2 || cores <= 2;

  // WebKit can parse url() without rendering SVG backdrop reference filters,
  // so refraction is limited to Chromium; everything else keeps the CSS blur.
  const canRefract = /Chrome|Chromium|Edg\//.test(navigator.userAgent)
    && !!(window.CSS && CSS.supports && CSS.supports('backdrop-filter', 'url("#dv-glass-probe")'))
    && 'ResizeObserver' in window
    && !veryLowEnd;

  html.setAttribute('data-dv-glass-optics', canRefract ? 'refraction' : 'blur');

  const rebuilders = new Set();
  const syncPreference = () => {
    html.classList.toggle('dv-opaque-glass', isOpaque());
    rebuilders.forEach((rebuild) => rebuild(true));
  };
  window.DV.onMediaChange(reduceTransparency, syncPreference);
  window.DV.onMediaChange(highContrast, syncPreference);
  html.classList.toggle('dv-opaque-glass', isOpaque());

  let defs = null;
  const ensureDefs = () => {
    if (defs && defs.isConnected) return defs;
    const svg = svgEl('svg', { 'aria-hidden': 'true', focusable: 'false', width: 0, height: 0 });
    svg.classList.add('dv-glass-filter-defs');
    defs = svgEl('defs');
    svg.append(defs);
    doc.body.append(svg);
    return defs;
  };

  let counter = 0;

  const mount = (target) => {
    let material = [...target.children].find((child) => child.classList.contains('dv-glass-material'));
    const injected = !material;
    if (injected) {
      material = doc.createElement('span');
      material.className = 'dv-glass-material';
      material.setAttribute('aria-hidden', 'true');
      target.prepend(material);
    }
    target.classList.add('dv-glass-mounted');

    const unmount = () => {
      target.classList.remove('dv-glass-mounted');
      if (injected) material.remove();
      else material.style.removeProperty('--dv-optical-filter');
    };

    if (!canRefract) return unmount;

    const id = `dv-optics-${++counter}`;
    const number = (name, fallback) => {
      const value = Number.parseFloat(target.getAttribute(name));
      return Number.isFinite(value) ? value : fallback;
    };
    const frost = number('data-dv-glass', 27) || 27;
    const rimOption = number('data-dv-glass-rim', 0);
    const bend = number('data-dv-glass-bend', 28);
    const dispersion = lowEnd ? 0 : Math.max(0, Math.min(0.3, number('data-dv-glass-dispersion', 0)));
    const saturate = number('data-dv-glass-saturate', 1);
    const custom = rimOption > 0 || dispersion > 0 || saturate !== 1;
    // Custom optics only: how much of the lens band stays sharp before it
    // melts into the frost (0..1 of the band; default .42).
    const clear = Math.max(0.1, Math.min(0.9, number('data-dv-glass-clear', 0.42)));

    const filter = svgEl('filter', { id, x: 0, y: 0, filterUnits: 'userSpaceOnUse', 'color-interpolation-filters': 'sRGB' });
    const vector = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'vector' });
    const rim = svgEl('feImage', { x: 0, y: 0, preserveAspectRatio: 'none', result: 'rim' });
    // Displacement passes and their share of the scale (the lens sets the
    // scale per size, see rebuild).
    const passes = [];
    const displace = (share, result) => {
      const node = svgEl('feDisplacementMap', { in: 'SourceGraphic', in2: 'vector', scale: bend * share, xChannelSelector: 'R', yChannelSelector: 'G', result });
      passes.push([node, share]);
      return node;
    };
    filter.append(vector);
    if (dispersion > 0) {
      // Three passes, one colour channel each, added back together.
      const only = (channel) => ['1 0 0 0 0', '0 1 0 0 0', '0 0 1 0 0']
        .map((row, index) => (index === channel ? row : '0 0 0 0 0'))
        .concat('0 0 0 1 0')
        .join(' ');
      filter.append(
        displace(1 + dispersion, 'bent-r'),
        displace(1, 'bent-g'),
        displace(1 - dispersion, 'bent-b'),
        svgEl('feColorMatrix', { in: 'bent-r', type: 'matrix', values: only(0), result: 'red' }),
        svgEl('feColorMatrix', { in: 'bent-g', type: 'matrix', values: only(1), result: 'green' }),
        svgEl('feColorMatrix', { in: 'bent-b', type: 'matrix', values: only(2), result: 'blue' }),
        svgEl('feComposite', { in: 'red', in2: 'green', operator: 'arithmetic', k1: 0, k2: 1, k3: 1, k4: 0, result: 'red-green' }),
        svgEl('feComposite', { in: 'red-green', in2: 'blue', operator: 'arithmetic', k1: 0, k2: 1, k3: 1, k4: 0, result: 'refracted' }),
      );
    } else {
      filter.append(displace(1, 'refracted'));
    }
    filter.append(
      svgEl('feGaussianBlur', { in: 'refracted', stdDeviation: 0.65, result: 'polished' }),
      rim,
      // Default optics frost the untouched backdrop (as before); custom optics
      // frost the refracted one, so the lens is continuous.
      svgEl('feGaussianBlur', { in: custom ? (dispersion > 0 ? 'bent-g' : 'refracted') : 'SourceGraphic', stdDeviation: frost, result: 'frosted' }),
      svgEl('feComposite', { in: 'polished', in2: 'rim', operator: 'in', result: 'edge' }),
      svgEl('feComposite', { in: 'frosted', in2: 'rim', operator: 'out', result: 'centre' }),
    );
    const merge = svgEl('feMerge', saturate !== 1 ? { result: 'glass' } : {});
    merge.append(svgEl('feMergeNode', { in: 'centre' }), svgEl('feMergeNode', { in: 'edge' }));
    filter.append(merge);
    if (saturate !== 1) filter.append(svgEl('feColorMatrix', { in: 'glass', type: 'saturate', values: saturate }));
    ensureDefs().append(filter);

    /* Default optics (every pill without custom attributes, unchanged since
       the MVP): the rim is pushed inwards by (1 - t)^2 of the full scale. */
    const paintDefault = (w, h, displacement, edge) => {
      const radius = h / 2;
      let ring = Math.min(12, h * 0.2);
      if (rimOption > 0) ring = Math.min(radius - 1, rimOption < 1 ? h * rimOption : rimOption);
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const cx = Math.max(radius, Math.min(w - radius, x + 0.5));
          const dx = x + 0.5 - cx;
          const dy = y + 0.5 - radius;
          const distance = Math.hypot(dx, dy);
          const depth = radius - distance;
          const t = Math.max(0, Math.min(1, depth / ring));
          const weight = depth >= 0 ? (1 - t) ** 2 : 0;
          const i = (y * w + x) * 4;
          displacement.data[i] = 128 - (distance ? dx / distance : 0) * 124 * weight;
          displacement.data[i + 1] = 128 - (distance ? dy / distance : 0) * 124 * weight;
          displacement.data[i + 2] = 128;
          displacement.data[i + 3] = 255;
          // Smoothly blend the sharp refracting lip into the softer centre.
          const blend = Math.max(0, Math.min(1, (t - 0.38) / 0.62));
          edge.data[i] = 255;
          edge.data[i + 1] = 255;
          edge.data[i + 2] = 255;
          edge.data[i + 3] = depth >= 0 ? Math.round(255 * (1 - blend * blend * (3 - 2 * blend))) : 0;
        }
      }
      return bend;
    };

    /* Custom optics (round 6, "liquid" lens): a convex band along the edge.
       Every point of the band shows the backdrop from a little further in:
       s(d) = d + o(d), d = depth from the edge. The shift o falls from its
       maximum at the edge to 0 at the inner end of the band with a smooth
       join (o' = 0 there), and the mapping never turns back (s' >= FLOOR):
       the band magnifies and bends the backdrop but never mirrors it, however
       strong the bend. The edge shift is capped at that limit, so
       data-dv-glass-bend can be a share of the limit (<= 1) or pixels.
       With rim .5 the whole height is lens: the pill shows the middle of
       what is under it, magnified, more towards the edges. */
    const FLOOR = 0.16;
    const paintLens = (w, h, displacement, edge) => {
      const radius = h / 2;
      let want = Math.min(12, h * 0.2); // no rim given: the default lip
      if (rimOption > 0) want = rimOption < 1 ? h * rimOption : rimOption;
      const ring = Math.max(2, Math.min(radius, want));
      // o(u) = shift * 1.5 * ((1 - u) - (1 - u^3) / 3), u = depth / ring:
      // o(0) = shift, o'(0) = -1.5 * shift / ring, o(1) = o'(1) = 0.
      const limit = ((2 / 3) * (1 - FLOOR) * ring) / (1 + dispersion);
      const shift = bend <= 1 ? Math.max(0, bend) * limit : Math.min(limit, bend);
      const scale = (shift * 2) / 0.96; // the map spans .02 .. .98
      for (let y = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1) {
          const cx = Math.max(radius, Math.min(w - radius, x + 0.5));
          const dx = x + 0.5 - cx;
          const dy = y + 0.5 - radius;
          const distance = Math.hypot(dx, dy);
          const depth = radius - distance;
          const u = Math.max(0, Math.min(1, depth / ring));
          const offset = depth >= 0 && scale ? (shift * 1.5 * ((1 - u) - (1 - u * u * u) / 3)) / scale : 0;
          const i = (y * w + x) * 4;
          // Sample inwards: against the outward normal.
          displacement.data[i] = Math.round(255 * (0.5 - (distance ? dx / distance : 0) * offset));
          displacement.data[i + 1] = Math.round(255 * (0.5 - (distance ? dy / distance : 0) * offset));
          displacement.data[i + 2] = 128;
          displacement.data[i + 3] = 255;
          // Sharp refraction in the outer part of the band, then the frost.
          const blend = Math.max(0, Math.min(1, (u - clear) / (1 - clear)));
          edge.data[i] = 255;
          edge.data[i + 1] = 255;
          edge.data[i + 2] = 255;
          edge.data[i + 3] = depth >= 0 ? Math.round(255 * (1 - blend * blend * (3 - 2 * blend))) : 0;
        }
      }
      return scale;
    };

    let previousSize = '';
    const rebuild = (force) => {
      if (isOpaque()) {
        material.style.removeProperty('--dv-optical-filter');
        previousSize = '';
        return;
      }
      const w = Math.round(target.clientWidth);
      const h = Math.round(target.clientHeight);
      if (!w || !h || (!force && `${w}:${h}` === previousSize)) return;
      previousSize = `${w}:${h}`;
      const canvas = doc.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const context = canvas.getContext('2d');
      if (!context) return;
      const displacement = context.createImageData(w, h);
      const edge = context.createImageData(w, h);
      const scale = (custom ? paintLens : paintDefault)(w, h, displacement, edge);
      passes.forEach(([node, share]) => node.setAttribute('scale', (scale * share).toFixed(2)));
      context.putImageData(displacement, 0, 0);
      vector.setAttribute('href', canvas.toDataURL());
      context.putImageData(edge, 0, 0);
      rim.setAttribute('href', canvas.toDataURL());
      [filter, vector, rim].forEach((node) => {
        node.setAttribute('width', w);
        node.setAttribute('height', h);
      });
      material.style.setProperty('--dv-optical-filter', `url("#${id}")`);
    };

    const observer = new ResizeObserver(() => rebuild(false));
    observer.observe(target);
    rebuild(false);
    rebuilders.add(rebuild);

    return () => {
      observer.disconnect();
      rebuilders.delete(rebuild);
      filter.remove();
      unmount();
    };
  };

  window.DV.register('dv-glass', (root) => {
    const cleanups = window.DV.queryAll(root, '[data-dv-glass]').map(mount);
    if (!cleanups.length) return undefined;
    return () => cleanups.forEach((fn) => fn());
  });
})();
